import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppLayout } from '../../layouts/AppLayout';
import { PageHeader } from '../../layouts/PageHeader';
import { Alert } from '../../common/Alert';
import { Button } from '../../common/Button';
import { useOrgBillingAnalysis } from '../../../hooks/useOrgBillingAnalysis';
import { useGradeBillingAnalysis } from '../../../hooks/useGradeBillingAnalysis';
import { useProjectBillingAnalysis } from '../../../hooks/useProjectBillingAnalysis';
import { useAccountOwnerData } from '../../../hooks/useAccountOwnerData';
import { mapAccountOwnerTimesheetToOrgBilling } from '../../../utils/mapAccountOwnerTimesheetToOrgBilling';
import {
  type PeriodFilterState,
  filterByDate,
  filterByDeptTech,
  filterBySelectedTechnologies,
  filterBySelectedGrades,
  filterBySelectedProjects,
  filterBySelectedAccountOwners,
  filterProjectsBySelectedTechnologies,
  computeTotals,
  buildChartByTech,
  buildChartByGrade,
  buildChartByProject,
  buildChartByAccountOwner,
  buildChartByMonth,
  buildDrillDownByTechnology,
  buildDrillDownByGrade,
  buildDrillDownByProject,
  buildDrillDownByAccountOwnerHierarchy,
  uniqueTechnologies,
  uniqueGrades,
  uniqueProjects,
  uniqueAccountOwners,
  fyStartYearsFromRecords,
} from '../../../utils/orgBillingAnalysisUtils';
import { TechnologyBillingKpiStrip } from './TechnologyBillingKpiStrip';
import { TechnologyBillingFilters } from './TechnologyBillingFilters';
import { TechnologyBillingChartsSection } from './TechnologyBillingChartsSection';
import { TechnologyBillingDrillDownTable } from './TechnologyBillingDrillDownTable';

type TimesheetInsightTab =
  | 'technology'
  | 'grade'
  | 'project'
  | 'accountOwner';

const PAGE_TITLE = 'Timesheet Insights';
const PRIMARY_CHART_TOP = 12;
const PROJECT_NAME_DISPLAY_MAX = 30;

type TabDef = [TimesheetInsightTab, string, number];

const TimesheetInsightsTabBar: React.FC<{
  active: TimesheetInsightTab;
  onChange: (t: TimesheetInsightTab) => void;
  tabs: TabDef[];
}> = ({ active, onChange, tabs }) => (
  <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-fit flex-wrap">
    {tabs.map(([key, label, count]) => (
      <button
        key={key}
        type="button"
        onClick={() => onChange(key)}
        className={`flex items-center gap-2 px-4 py-2 rounded-md text-[13px] font-semibold transition-colors ${
          active === key
            ? 'bg-white text-slate-800 shadow-sm'
            : 'text-slate-500 hover:text-slate-700'
        }`}
      >
        {label}
        <span
          className={`text-[11px] px-1.5 py-0.5 rounded font-bold ${
            active === key
              ? 'bg-blue-50 text-blue-700'
              : 'bg-slate-200 text-slate-500'
          }`}
        >
          {count}
        </span>
      </button>
    ))}
  </div>
);

function formatHours(n: number): string {
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

const SkeletonBlock: React.FC<{ className?: string }> = ({
  className = 'h-24',
}) => <div className={`rounded-xl bg-slate-200/80 animate-pulse ${className}`} />;

const LoadingBody: React.FC = () => (
  <div className="p-6 space-y-4 w-full min-w-0">
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <SkeletonBlock key={i} className="h-28" />
      ))}
    </div>
    <SkeletonBlock className="h-80" />
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <SkeletonBlock className="h-72" />
      <SkeletonBlock className="h-72" />
    </div>
  </div>
);

export const TechnologyBillingOverviewPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const category = searchParams.get('category');
  const useCase = searchParams.get('useCase');

  const { records, loading, error, refetch } = useOrgBillingAnalysis();
  const {
    records: gradeRecords,
    loading: gradeLoading,
    error: gradeError,
    refetch: refetchGrade,
  } = useGradeBillingAnalysis();
  const {
    records: projectRecords,
    loading: projectLoading,
    error: projectError,
    refetch: refetchProject,
  } = useProjectBillingAnalysis();
  const {
    records: amTimesheetRecords,
    projectToClient,
    loading: amLoading,
    error: amError,
    refetch: refetchAm,
  } = useAccountOwnerData();
  const [periodType, setPeriodType] = useState<'fy_india' | 'calendar'>(
    'fy_india',
  );
  const [fyYear, setFyYear] = useState('all');
  const [fyQuarter, setFyQuarter] = useState('all');
  const [tsYear, setTsYear] = useState('all');
  const [tsMonth, setTsMonth] = useState('all');
  const [tsYearEnd] = useState('all');
  const [tsMonthEnd] = useState('all');
  const [fyDefaultSet, setFyDefaultSet] = useState(false);
  const calendarDefaultSetRef = useRef(false);
  const [activeTab, setActiveTab] = useState<TimesheetInsightTab>('project');
  const [selectedTechnologies, setSelectedTechnologies] = useState<string[]>(
    [],
  );
  const [selectedGrades, setSelectedGrades] = useState<string[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [selectedProjectTechnologies, setSelectedProjectTechnologies] =
    useState<string[]>([]);
  const [selectedAccountOwners, setSelectedAccountOwners] = useState<string[]>(
    [],
  );

  const periodState: PeriodFilterState = useMemo(
    () => ({
      periodType,
      fyYear,
      fyQuarter,
      tsYear,
      tsMonth,
      tsYearEnd,
      tsMonthEnd,
    }),
    [
      periodType,
      fyYear,
      fyQuarter,
      tsYear,
      tsMonth,
      tsYearEnd,
      tsMonthEnd,
    ],
  );

  useEffect(() => {
    if (!records.length || fyDefaultSet) return;
    const sorted = fyStartYearsFromRecords(records);
    const now = new Date();
    const currentFYStart =
      now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    setFyYear(
      sorted.length > 0 ? String(sorted[0]) : String(currentFYStart),
    );
    setFyDefaultSet(true);
  }, [records, fyDefaultSet]);

  useEffect(() => {
    if (periodType === 'calendar' && !calendarDefaultSetRef.current) {
      setTsYear(String(new Date().getFullYear()));
      calendarDefaultSetRef.current = true;
    }
    if (periodType === 'fy_india') {
      calendarDefaultSetRef.current = false;
    }
  }, [periodType]);

  const dateFilteredRecords = useMemo(
    () => filterByDate(records, periodState),
    [records, periodState],
  );

  const dateFilteredGradeRecords = useMemo(
    () => filterByDate(gradeRecords, periodState),
    [gradeRecords, periodState],
  );

  const dateFilteredProjectRecords = useMemo(
    () => filterByDate(projectRecords, periodState),
    [projectRecords, periodState],
  );

  const dateBaseRecords = useMemo(
    () => filterByDeptTech(dateFilteredRecords, 'all', 'all'),
    [dateFilteredRecords],
  );

  const gradeDateBaseRecords = useMemo(
    () => filterByDeptTech(dateFilteredGradeRecords, 'all', 'all'),
    [dateFilteredGradeRecords],
  );

  const projectDateBaseRecords = useMemo(
    () => filterByDeptTech(dateFilteredProjectRecords, 'all', 'all'),
    [dateFilteredProjectRecords],
  );

  const amOrgRecords = useMemo(
    () =>
      mapAccountOwnerTimesheetToOrgBilling(
        amTimesheetRecords,
        projectToClient,
      ),
    [amTimesheetRecords, projectToClient],
  );

  const dateFilteredAmRecords = useMemo(
    () => filterByDate(amOrgRecords, periodState),
    [amOrgRecords, periodState],
  );

  const amDateBaseRecords = useMemo(
    () => filterByDeptTech(dateFilteredAmRecords, 'all', 'all'),
    [dateFilteredAmRecords],
  );

  const technologyFilteredRecords = useMemo(
    () =>
      filterBySelectedTechnologies(dateBaseRecords, selectedTechnologies),
    [dateBaseRecords, selectedTechnologies],
  );

  const gradeFilteredRecords = useMemo(
    () =>
      filterBySelectedGrades(gradeDateBaseRecords, selectedGrades),
    [gradeDateBaseRecords, selectedGrades],
  );

  const projectScopeRecords = useMemo(
    () =>
      filterProjectsBySelectedTechnologies(
        projectDateBaseRecords,
        selectedProjectTechnologies,
      ),
    [projectDateBaseRecords, selectedProjectTechnologies],
  );

  const projectFilteredRecords = useMemo(
    () =>
      filterBySelectedProjects(projectScopeRecords, selectedProjects),
    [projectScopeRecords, selectedProjects],
  );

  const amFilteredRecords = useMemo(
    () =>
      filterBySelectedAccountOwners(
        amDateBaseRecords,
        selectedAccountOwners,
      ),
    [amDateBaseRecords, selectedAccountOwners],
  );

  const techTotals = useMemo(
    () => computeTotals(technologyFilteredRecords),
    [technologyFilteredRecords],
  );

  const primaryChartRows = useMemo(
    () => buildChartByTech(technologyFilteredRecords),
    [technologyFilteredRecords],
  );

  const chartByMonth = useMemo(
    () => buildChartByMonth(technologyFilteredRecords),
    [technologyFilteredRecords],
  );

  const gradePrimaryChartRows = useMemo(
    () => buildChartByGrade(gradeFilteredRecords),
    [gradeFilteredRecords],
  );

  const gradeChartByMonth = useMemo(
    () => buildChartByMonth(gradeFilteredRecords),
    [gradeFilteredRecords],
  );

  const projectPrimaryChartRows = useMemo(
    () => buildChartByProject(projectFilteredRecords),
    [projectFilteredRecords],
  );

  const projectChartByMonth = useMemo(
    () => buildChartByMonth(projectFilteredRecords),
    [projectFilteredRecords],
  );

  const amPrimaryChartRows = useMemo(
    () => buildChartByAccountOwner(amFilteredRecords),
    [amFilteredRecords],
  );

  const amChartByMonth = useMemo(
    () => buildChartByMonth(amFilteredRecords),
    [amFilteredRecords],
  );

  const technologyOptions = useMemo(
    () => uniqueTechnologies(dateFilteredRecords, 'all'),
    [dateFilteredRecords],
  );

  const gradeFilterOptions = useMemo(
    () => uniqueGrades(dateFilteredGradeRecords),
    [dateFilteredGradeRecords],
  );

  const projectTechFilterOptions = useMemo(
    () => uniqueTechnologies(projectDateBaseRecords, 'all'),
    [projectDateBaseRecords],
  );

  const projectFilterOptions = useMemo(
    () => uniqueProjects(projectScopeRecords),
    [projectScopeRecords],
  );

  const amOwnerFilterOptions = useMemo(
    () => uniqueAccountOwners(amDateBaseRecords),
    [amDateBaseRecords],
  );

  const insightTabCounts = useMemo((): TabDef[] => {
    const techCount = Object.keys(
      buildDrillDownByTechnology(dateBaseRecords),
    ).length;
    const gradeCount = Object.keys(
      buildDrillDownByGrade(gradeDateBaseRecords),
    ).length;
    const projectCount = Object.keys(
      buildDrillDownByProject(projectScopeRecords),
    ).length;
    const amCount = Object.keys(
      buildDrillDownByAccountOwnerHierarchy(amDateBaseRecords),
    ).length;
    return [
      ['project', 'Project Wise', projectCount],
      ['technology', 'Technology Wise', techCount],
      ['grade', 'Grade Wise', gradeCount],
      ['accountOwner', 'Account owner wise', amCount],
    ];
  }, [
    dateBaseRecords,
    gradeDateBaseRecords,
    projectScopeRecords,
    amDateBaseRecords,
  ]);

  useEffect(() => {
    const optSet = new Set(technologyOptions);
    setSelectedTechnologies((prev) => {
      const next = prev.filter((t) => optSet.has(t));
      return next.length === prev.length ? prev : next;
    });
  }, [technologyOptions]);

  useEffect(() => {
    const optSet = new Set(gradeFilterOptions);
    setSelectedGrades((prev) => {
      const next = prev.filter((t) => optSet.has(t));
      return next.length === prev.length ? prev : next;
    });
  }, [gradeFilterOptions]);

  useEffect(() => {
    const optSet = new Set(projectFilterOptions);
    setSelectedProjects((prev) => {
      const next = prev.filter((t) => optSet.has(t));
      return next.length === prev.length ? prev : next;
    });
  }, [projectFilterOptions]);

  useEffect(() => {
    const optSet = new Set(projectTechFilterOptions);
    setSelectedProjectTechnologies((prev) => {
      const next = prev.filter((t) => optSet.has(t));
      return next.length === prev.length ? prev : next;
    });
  }, [projectTechFilterOptions]);

  useEffect(() => {
    const optSet = new Set(amOwnerFilterOptions);
    setSelectedAccountOwners((prev) => {
      const next = prev.filter((t) => optSet.has(t));
      return next.length === prev.length ? prev : next;
    });
  }, [amOwnerFilterOptions]);

  const fyYearOptions = useMemo(
    () => fyStartYearsFromRecords(records),
    [records],
  );

  const calendarYearOptions = useMemo(() => {
    const ys = new Set<number>();
    records.forEach((r) => ys.add(Number(r.year)));
    return [...ys].filter((y) => !isNaN(y)).sort((a, b) => b - a);
  }, [records]);

  const periodLabel =
    periodType === 'calendar'
      ? tsYear !== 'all'
        ? tsYear +
          (tsMonth !== 'all' ? `-${String(tsMonth).padStart(2, '0')}` : '') +
          (tsYearEnd !== 'all' && tsMonthEnd !== 'all'
            ? ` → ${tsYearEnd}-${String(tsMonthEnd).padStart(2, '0')}`
            : '')
        : 'All time'
      : fyYear !== 'all'
        ? `FY ${fyYear}-${String(Number(fyYear) + 1).slice(-2)}` +
          (fyQuarter !== 'all' ? ` · ${fyQuarter}` : '')
        : 'All time';

  const pageSubtitle =
    category && useCase ? `Legacy: ${category} · ${useCase}` : undefined;

  const handleReset = () => {
    setPeriodType('fy_india');
    const now = new Date();
    const cy =
      now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    const opts = fyStartYearsFromRecords(records);
    setFyYear(opts[0] != null ? String(opts[0]) : String(cy));
    setFyQuarter('all');
    setTsYear('all');
    setTsMonth('all');
    setSelectedTechnologies([]);
    setSelectedGrades([]);
    setSelectedProjects([]);
    setSelectedProjectTechnologies([]);
    setSelectedAccountOwners([]);
  };

  const filters = (
    <TechnologyBillingFilters
      periodType={periodType}
      onPeriodType={setPeriodType}
      fyYear={fyYear}
      onFyYear={setFyYear}
      fyYearOptions={fyYearOptions}
      fyQuarter={fyQuarter}
      onFyQuarter={setFyQuarter}
      tsYear={tsYear}
      onTsYear={setTsYear}
      calendarYearOptions={calendarYearOptions}
      tsMonth={tsMonth}
      onTsMonth={setTsMonth}
      onReset={handleReset}
    />
  );

  if (loading) {
    return (
      <AppLayout>
        <PageHeader
          title={PAGE_TITLE}
          {...(pageSubtitle ? { subtitle: pageSubtitle } : {})}
          filters={filters}
        />
        <LoadingBody />
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <PageHeader title={PAGE_TITLE} />
        <div className="p-6 max-w-lg">
          <Alert type="error" title="Could not load data" message={error} />
          <Button className="mt-4" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </AppLayout>
    );
  }

  if (records.length === 0) {
    return (
      <AppLayout>
        <PageHeader
          title={PAGE_TITLE}
          {...(pageSubtitle ? { subtitle: pageSubtitle } : {})}
        />
        <div className="p-6 max-w-lg">
          <Alert
            type="info"
            title="No rows"
            message="org_billing_analysis.json is empty or missing. Add data under public/ or public/json-data/."
          />
          <Button variant="secondary" className="mt-4" onClick={() => refetch()}>
            Reload
          </Button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageHeader
        title={PAGE_TITLE}
        {...(pageSubtitle ? { subtitle: pageSubtitle } : {})}
        filters={filters}
      />

      <div className="p-6 space-y-6 w-full min-w-0 pb-12">
        <TechnologyBillingKpiStrip
          billable={techTotals.billable}
          nonBillable={techTotals.nonBillable}
          productivityPct={techTotals.productivity}
          resourceCount={techTotals.resourceCount}
          periodSummary={periodLabel}
          recordCount={technologyFilteredRecords.length}
        />

        <div className="space-y-4">
          <TimesheetInsightsTabBar
            active={activeTab}
            onChange={setActiveTab}
            tabs={insightTabCounts}
          />

          {activeTab === 'project' ? (
            projectLoading ? (
              <SkeletonBlock className="h-80" />
            ) : projectError ? (
              <div className="max-w-lg space-y-3">
                <Alert
                  type="error"
                  title="Could not load project billing data"
                  message={projectError}
                />
                <Button onClick={() => refetchProject()}>Retry</Button>
              </div>
            ) : projectRecords.length === 0 ? (
              <div className="max-w-lg">
                <Alert
                  type="info"
                  title="No project billing rows"
                  message="Run npm run generate-project-billing-analysis (uses ts_project_json gold_fact_project_user_monthly_project_id_*.json, UC-T7 lineage)."
                />
              </div>
            ) : (
              <>
                <TechnologyBillingDrillDownTable
                  variant="project"
                  filteredRecords={projectFilteredRecords}
                  selectedTechnologies={selectedProjects}
                  onSelectedTechnologiesChange={setSelectedProjects}
                  technologyOptions={projectFilterOptions}
                  truncateSliceNameAt={PROJECT_NAME_DISPLAY_MAX}
                  projectTechnologyFilterSelected={selectedProjectTechnologies}
                  onProjectTechnologyFilterChange={setSelectedProjectTechnologies}
                  projectTechnologyFilterOptions={projectTechFilterOptions}
                />

                <TechnologyBillingChartsSection
                  primaryChartRows={projectPrimaryChartRows}
                  primaryChartTitle={`Hours by project (top ${PRIMARY_CHART_TOP})`}
                  chartByMonth={projectChartByMonth}
                  topPrimaryLimit={PRIMARY_CHART_TOP}
                  formatHours={formatHours}
                  primaryYTickTruncateAt={PROJECT_NAME_DISPLAY_MAX}
                />
              </>
            )
          ) : activeTab === 'technology' ? (
            <>
              <TechnologyBillingDrillDownTable
                filteredRecords={technologyFilteredRecords}
                selectedTechnologies={selectedTechnologies}
                onSelectedTechnologiesChange={setSelectedTechnologies}
                technologyOptions={technologyOptions}
              />

              <TechnologyBillingChartsSection
                primaryChartRows={primaryChartRows}
                primaryChartTitle={`Hours by technology (top ${PRIMARY_CHART_TOP})`}
                chartByMonth={chartByMonth}
                topPrimaryLimit={PRIMARY_CHART_TOP}
                formatHours={formatHours}
              />
            </>
          ) : activeTab === 'grade' ? (
            gradeLoading ? (
              <SkeletonBlock className="h-80" />
            ) : gradeError ? (
              <div className="max-w-lg space-y-3">
                <Alert
                  type="error"
                  title="Could not load grade billing data"
                  message={gradeError}
                />
                <Button onClick={() => refetchGrade()}>Retry</Button>
              </div>
            ) : gradeRecords.length === 0 ? (
              <div className="max-w-lg">
                <Alert
                  type="info"
                  title="No grade billing rows"
                  message="Generate grade_billing_analysis.json (see UC-T10 / generate-grade-billing-analysis.js)."
                />
              </div>
            ) : (
              <>
                <TechnologyBillingDrillDownTable
                  variant="grade"
                  filteredRecords={gradeFilteredRecords}
                  selectedTechnologies={selectedGrades}
                  onSelectedTechnologiesChange={setSelectedGrades}
                  technologyOptions={gradeFilterOptions}
                />

                <TechnologyBillingChartsSection
                  primaryChartRows={gradePrimaryChartRows}
                  primaryChartTitle={`Hours by grade (top ${PRIMARY_CHART_TOP})`}
                  chartByMonth={gradeChartByMonth}
                  topPrimaryLimit={PRIMARY_CHART_TOP}
                  formatHours={formatHours}
                />
              </>
            )
          ) : activeTab === 'accountOwner' ? (
            amLoading ? (
              <SkeletonBlock className="h-80" />
            ) : amError ? (
              <div className="max-w-lg space-y-3">
                <Alert
                  type="error"
                  title="Could not load account owner timesheet data"
                  message={amError}
                />
                <Button onClick={() => refetchAm()}>Retry</Button>
              </div>
            ) : amTimesheetRecords.length === 0 ? (
              <div className="max-w-lg">
                <Alert
                  type="info"
                  title="No account owner timesheet rows"
                  message="Ensure jsonbyAM/am_manifest.json and per-AM files exist (UC-T8 / RMG portfolio lineage)."
                />
              </div>
            ) : (
              <>
                <TechnologyBillingDrillDownTable
                  variant="accountOwner"
                  filteredRecords={amFilteredRecords}
                  selectedTechnologies={selectedAccountOwners}
                  onSelectedTechnologiesChange={setSelectedAccountOwners}
                  technologyOptions={amOwnerFilterOptions}
                  truncateSliceNameAt={PROJECT_NAME_DISPLAY_MAX}
                />

                <TechnologyBillingChartsSection
                  primaryChartRows={amPrimaryChartRows}
                  primaryChartTitle={`Hours by account owner (top ${PRIMARY_CHART_TOP})`}
                  chartByMonth={amChartByMonth}
                  topPrimaryLimit={PRIMARY_CHART_TOP}
                  formatHours={formatHours}
                />
              </>
            )
          ) : null}
        </div>
      </div>
    </AppLayout>
  );
};

export default TechnologyBillingOverviewPage;
